class VideoProductCards extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.cards = Array.from(this.querySelectorAll('[data-card]'));
    this.prevButton = this.querySelector('[data-nav-prev]');
    this.nextButton = this.querySelector('[data-nav-next]');
    this.autoplay = this.dataset.autoplay !== 'false';

    this.onCardClick = this.onCardClick.bind(this);
    this.onPrevClick = this.onPrevClick.bind(this);
    this.onNextClick = this.onNextClick.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onBlockSelect = this.onBlockSelect.bind(this);

    this.addEventListener('click', this.onCardClick);
    this.prevButton?.addEventListener('click', this.onPrevClick);
    this.nextButton?.addEventListener('click', this.onNextClick);
    this.track?.addEventListener('scroll', this.onScroll, { passive: true });
    document.addEventListener('shopify:block:select', this.onBlockSelect);

    this.cards.forEach((card) => this.syncCard(card));
    this.updateNav();
    this.observeCards();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onCardClick);
    this.prevButton?.removeEventListener('click', this.onPrevClick);
    this.nextButton?.removeEventListener('click', this.onNextClick);
    this.track?.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('shopify:block:select', this.onBlockSelect);
    this.observer?.disconnect();
  }

  /* Autoplay the card that is on screen, one at a time, so several videos
     never decode or play at once. */
  observeCards() {
    if (!this.autoplay || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        /* Cards leaving the viewport are handled first, so the one scrolling
           in finds the spot free and can take over. */
        const ordered = [...entries].sort(
          (a, b) => Number(a.isIntersecting) - Number(b.isIntersecting)
        );

        ordered.forEach((entry) => {
          const card = entry.target;
          const video = card.querySelector('video');
          if (!video) return;

          if (!entry.isIntersecting) {
            video.pause();
            this.syncCard(card);
            return;
          }

          /* A card the visitor paused by hand stays paused, and a video that
             is already running is never interrupted by one coming into view. */
          if (card.dataset.userPaused === 'true') return;
          if (this.playingCard()) return;

          this.play(card, video);
        });
      },
      { threshold: 0.5 }
    );

    this.cards.forEach((card) => this.observer.observe(card));
  }

  onCardClick(event) {
    const card = event.target.closest('[data-card]');
    if (!card || !this.contains(card)) return;

    const video = card.querySelector('video');
    if (!video) return;

    const button = event.target.closest('[data-control]');

    if (button) {
      event.preventDefault();

      if (button.dataset.control === 'play') {
        this.toggle(card, video);
      }

      if (button.dataset.control === 'sound') {
        video.muted = !video.muted;
        /* Sound on one card silences the others so audio never overlaps. */
        if (!video.muted) this.muteOthers(card);
        this.syncCard(card);
      }

      return;
    }

    /* A click anywhere else on the card does what the play button does: start
       the video, or stop it when it is already running. The product link keeps
       its own click so the card stays shoppable. */
    if (event.target.closest('a')) return;

    this.toggle(card, video);
  }

  toggle(card, video) {
    if (video.paused) {
      /* Starting a video stops whatever else was playing, see play(). */
      card.dataset.userPaused = 'false';
      this.play(card, video);
      return;
    }

    /* Stopped by hand, so it stays stopped until the visitor asks again. */
    card.dataset.userPaused = 'true';
    video.pause();
    this.syncCard(card);
  }

  play(card, video) {
    /* Only one video plays at a time, so starting this one stops the others. */
    this.pauseOthers(card);

    const played = video.play();
    if (played && typeof played.catch === 'function') {
      /* Autoplay can be refused (iOS low power mode); show the play icon instead. */
      played.catch(() => {}).then(() => this.syncCard(card));
    }
    this.syncCard(card);
  }

  pauseOthers(current) {
    this.cards.forEach((card) => {
      if (card === current) return;
      const video = card.querySelector('video');
      if (!video || video.paused) return;
      video.pause();
      this.syncCard(card);
    });
  }

  playingCard() {
    return this.cards.find((card) => {
      const video = card.querySelector('video');
      return video && !video.paused;
    });
  }

  muteOthers(current) {
    this.cards.forEach((card) => {
      if (card === current) return;
      const video = card.querySelector('video');
      if (!video || video.muted) return;
      video.muted = true;
      this.syncCard(card);
    });
  }

  syncCard(card) {
    const video = card.querySelector('video');
    if (!video) return;

    card.dataset.playing = video.paused ? 'false' : 'true';
    card.dataset.muted = video.muted ? 'true' : 'false';

    const playButton = card.querySelector('[data-control="play"]');
    playButton?.setAttribute('aria-pressed', video.paused ? 'false' : 'true');

    const soundButton = card.querySelector('[data-control="sound"]');
    soundButton?.setAttribute('aria-pressed', video.muted ? 'false' : 'true');
  }

  onPrevClick() {
    this.scrollBySlide(-1);
  }

  onNextClick() {
    this.scrollBySlide(1);
  }

  scrollBySlide(direction) {
    if (!this.track) return;
    const slide = this.track.querySelector('[data-slide]');
    if (!slide) return;

    const step = slide.getBoundingClientRect().width + this.slideGap();
    this.track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  slideGap() {
    const gap = parseFloat(getComputedStyle(this.track).columnGap);
    return Number.isNaN(gap) ? 0 : gap;
  }

  onScroll() {
    if (this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      this.updateNav();
    });
  }

  updateNav() {
    if (!this.track || !this.prevButton || !this.nextButton) return;

    const maxScroll = this.track.scrollWidth - this.track.clientWidth;
    const position = Math.abs(this.track.scrollLeft);

    this.prevButton.disabled = position <= 1;
    this.nextButton.disabled = position >= maxScroll - 1;
  }

  /* Scroll a block into view when it is selected in the theme editor. */
  onBlockSelect(event) {
    if (!this.contains(event.target)) return;
    const slide = event.target.closest('[data-slide]');
    slide?.scrollIntoView({ block: 'nearest', inline: 'center' });
  }
}

if (!customElements.get('video-product-cards')) {
  customElements.define('video-product-cards', VideoProductCards);
}
